/**
 * Slide-in edit panel for a single holding — opened from a row click in
 * HoldingsTable (#1533). Every field here is bound to the same react-hook-form
 * paths the old inline table row used, so save-on-blur semantics are unchanged;
 * this is purely an alternate rendering surface for the same form state.
 */

import { useEffect, useRef, useState } from 'react';
import { Controller, type Control, type UseFormRegister, type UseFormSetValue, type UseFormGetValues } from 'react-hook-form';
import { HoldingData } from '../../types/wizard';
import { formatNumber } from '../../utils/formatters';
import { FundMetadataResponse } from '../../services/planApi';
import { CurrencyInput, OcfInput, TickerAutocomplete, TickerLookupStatus } from './Step3Holdings';
import type { Step3InternalFormData } from './Step3Holdings';

/**
 * Book price per unit is never persisted directly — only the total `costBasis`
 * is. This mirrors OcfInput: display a derived value (costBasis / quantity),
 * write back the recomputed total on blur.
 */
function BookPriceInput({ id, quantity, costBasis, onCostBasisChange, onBlur }: Readonly<{
  id?: string;
  quantity: number | undefined;
  costBasis: number | undefined;
  onCostBasisChange: (total: number | undefined) => void;
  onBlur: () => void;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);
  // `quantity` can transiently be NaN — e.g. the user clears "Units held" to
  // retype it, which momentarily turns this field's own quantity prop into
  // NaN via RHF's valueAsNumber. Treat that the same as "not ready yet"
  // rather than as "known zero", so we never silently drop what's typed here
  // or let the resync effect below wipe it out mid-edit.
  const hasValidQuantity = typeof quantity === 'number' && quantity > 0;
  const derivedValue = hasValidQuantity && costBasis != null ? costBasis / quantity! : undefined;
  const externalValueRef = useRef(derivedValue);

  useEffect(() => {
    if (!hasValidQuantity) return;
    if (derivedValue !== externalValueRef.current && inputRef.current) {
      externalValueRef.current = derivedValue;
      inputRef.current.value = derivedValue != null ? formatNumber(derivedValue, 4) : '';
    }
    // Only re-sync when the derived value itself changes (e.g. a live fetch), not on every render.
  }, [derivedValue, hasValidQuantity]);

  const handleBlur = () => {
    // Quantity isn't in a usable state right now — leave whatever's typed
    // here alone rather than computing against a bogus figure.
    if (!hasValidQuantity) { onBlur(); return; }
    const text = inputRef.current?.value.replaceAll(',', '').trim() || '';
    if (text === '') {
      onCostBasisChange(undefined);
      onBlur();
      return;
    }
    const bookPrice = Number.parseFloat(text);
    if (!Number.isNaN(bookPrice) && bookPrice >= 0) {
      onCostBasisChange(bookPrice * quantity!);
    }
    onBlur();
  };

  return (
    <input
      id={id}
      type="text"
      ref={inputRef}
      className="form-input form-input-sm"
      placeholder={hasValidQuantity ? 'e.g., 0.9750' : 'Enter units held first'}
      disabled={!hasValidQuantity}
      title={hasValidQuantity ? undefined : 'Enter units held before setting a book price'}
      defaultValue={derivedValue != null ? formatNumber(derivedValue, 4) : ''}
      onBlur={handleBlur}
    />
  );
}

interface HoldingEditDrawerProps {
  isOpen: boolean;
  mode: 'edit' | 'add';
  personIndex: number;
  holdingIndex: number;
  fieldId: string;
  control: Control<Step3InternalFormData>;
  register: UseFormRegister<Step3InternalFormData>;
  setValue: UseFormSetValue<Step3InternalFormData>;
  getValues: UseFormGetValues<Step3InternalFormData>;
  holdingValues: HoldingData | undefined;
  etfNameError?: string;
  currentValueError?: string;
  lookupStatus?: TickerLookupStatus;
  onTickerChange: (value: string) => void;
  onTickerSelect: (fund: FundMetadataResponse) => void;
  onTickerBlur: () => void;
  onFieldBlur: () => void;
  onClose: () => void;
  onDelete: () => void;
  onSaveAndAddAnother: () => void;
  livePriceLoading: boolean;
  livePriceFailed: boolean;
  priceIsLive: boolean;
  livePriceInfo?: { exchange?: string; tradeCurrency?: string; tradePrice?: number };
  quantityEstimated: boolean;
  onEnableQuantityTracking: () => void;
  onRemoveQuantityTracking: () => void;
  onFetchLivePrice: () => void;
  onQuantityFieldChange: () => void;
  onUnitPriceFieldChange: () => void;
  onCostBasisChange: (total: number | undefined) => void;
}

/** Format a native (non-GBP) unit price with its currency symbol, e.g. "$250.00". */
function formatNativePrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function HoldingEditDrawer({
  isOpen,
  mode,
  personIndex,
  holdingIndex,
  fieldId,
  control,
  register,
  setValue,
  getValues,
  holdingValues,
  etfNameError,
  currentValueError,
  lookupStatus,
  onTickerChange,
  onTickerSelect,
  onTickerBlur,
  onFieldBlur,
  onClose,
  onDelete,
  onSaveAndAddAnother,
  livePriceLoading,
  livePriceFailed,
  priceIsLive,
  livePriceInfo,
  quantityEstimated,
  onEnableQuantityTracking,
  onRemoveQuantityTracking,
  onFetchLivePrice,
  onQuantityFieldChange,
  onUnitPriceFieldChange,
  onCostBasisChange,
}: Readonly<HoldingEditDrawerProps>) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmDeleteTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Escape closes the drawer; Tab is trapped within it while open.
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Autofocus the first field whenever the drawer opens for a (new) holding —
  // otherwise a keyboard-first user has to reach for the mouse every time,
  // which adds up fast when adding several holdings in a row.
  useEffect(() => {
    if (!isOpen) return;
    const first = drawerRef.current?.querySelector<HTMLElement>('input:not(:disabled), select:not(:disabled)');
    first?.focus();
  }, [isOpen, personIndex, holdingIndex]);

  // Reset the delete-confirm arming whenever a different holding is opened,
  // so it can never carry over and fire on the wrong holding.
  useEffect(() => {
    setConfirmDelete(false);
    return () => { if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current); };
  }, [personIndex, holdingIndex]);

  const handleDeleteClick = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      confirmDeleteTimerRef.current = setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    if (confirmDeleteTimerRef.current) clearTimeout(confirmDeleteTimerRef.current);
    onDelete();
  };

  if (!isOpen) return null;

  const prefix = `holdings.person${personIndex}.${holdingIndex}` as const;
  // Open the unit-count section whenever we have a unit price: the auto-fetch on
  // ticker-select sets it (so the user only enters their count), and "stop
  // tracking" clears it (collapsing back to the button) even though the sticky
  // quantityTracked flag stays set. (#1636 / #1531)
  const hasQuantityFields = holdingValues?.unitPrice != null;
  const isBond = holdingValues?.assetClass === 'BOND';
  const hasBondDetails = isBond && !!holdingValues?.bondDetails;
  const bondCoupon = holdingValues?.bondDetails?.annualCouponPct;
  const bondNominal = holdingValues?.bondDetails?.nominalValue;
  const nameErrorId = `drawer-name-error-${fieldId}`;
  const valueErrorId = `drawer-value-error-${fieldId}`;

  return (
    <>
      <button type="button" className="drawer-backdrop" aria-label="Close overlay" onClick={onClose} />
      <div ref={drawerRef} className="holding-drawer" role="dialog" aria-modal="true" aria-label={mode === 'add' ? 'Add investment' : 'Edit investment'}>
        <div className="drawer-header">
          <div>
            <h3>{mode === 'add' ? 'Add investment' : (holdingValues?.etfName || 'Edit investment')}</h3>
            <p className="drawer-sub">{holdingValues?.accountType} &middot; {holdingValues?.assetClass}</p>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="Close">&#10005;</button>
        </div>

        <div className="drawer-field">
          <label htmlFor={`drawer-type-${fieldId}`}>Account type</label>
          <select
            id={`drawer-type-${fieldId}`}
            className="form-input"
            {...register(`${prefix}.accountType`)}
            onBlur={onFieldBlur}
          >
            <option value="SIPP">SIPP</option>
            <option value="ISA">ISA</option>
            <option value="BROKERAGE">Brokerage</option>
          </select>
        </div>

        <div className="drawer-field">
          <label htmlFor={`drawer-ticker-${fieldId}`}>Ticker</label>
          <TickerAutocomplete
            value={getValues(`${prefix}.ticker`) || ''}
            onChange={onTickerChange}
            onSelect={onTickerSelect}
            onBlur={onTickerBlur}
            lookupStatus={lookupStatus}
          />
          {/* Rendered here, OUTSIDE the unit-count disclosure, so a failed
              auto-fetch on a freshly-selected ticker is visible even though its
              price section never opened (the price stays unset on failure, so
              hasQuantityFields is false and the section is collapsed). (B2) */}
          {livePriceFailed && (
            <p className="quantity-price-fetch-error">
              {hasQuantityFields
                ? 'Couldn’t fetch today’s price — kept your last entry. Please check or enter it manually.'
                : 'Couldn’t fetch today’s price for this ticker. Enter the price per unit manually below, or pick the ticker again.'}
            </p>
          )}
        </div>

        <div className="drawer-field">
          <label htmlFor={`drawer-name-${fieldId}`}>Fund name</label>
          <input
            id={`drawer-name-${fieldId}`}
            type="text"
            className={`form-input ${etfNameError ? 'error' : ''}`}
            placeholder="e.g., Vanguard FTSE All-World"
            aria-invalid={etfNameError ? true : undefined}
            aria-describedby={etfNameError ? nameErrorId : undefined}
            {...register(`${prefix}.etfName`)}
            onBlur={onFieldBlur}
          />
          {etfNameError && <span id={nameErrorId} className="field-error-inline">Fund name required</span>}
        </div>

        <div className="drawer-row">
          <div className="drawer-field">
            <label htmlFor={`drawer-class-${fieldId}`}>Asset class</label>
            {(() => {
              const { onChange: regOnChange, ...regRest } = register(`${prefix}.assetClass`);
              return (
                <select
                  id={`drawer-class-${fieldId}`}
                  className="form-input"
                  {...regRest}
                  onChange={(e) => {
                    void regOnChange(e);
                    if (e.target.value !== 'BOND') {
                      setValue(`${prefix}.bondDetails`, undefined);
                    }
                    onFieldBlur();
                  }}
                  onBlur={onFieldBlur}
                >
                  <option value="EQUITY">Equity</option>
                  <option value="BOND">Bond</option>
                  <option value="MIXED">Mixed / Balanced</option>
                  <option value="OTHER">Other</option>
                </select>
              );
            })()}
          </div>
          <div className="drawer-field">
            <label htmlFor={`drawer-ocf-${fieldId}`}>Annual fee (%)</label>
            <Controller
              name={`${prefix}.ocfPct`}
              control={control}
              render={({ field: ocfField }) => (
                <OcfInput
                  value={ocfField.value}
                  onChange={ocfField.onChange}
                  onBlur={() => { ocfField.onBlur(); onFieldBlur(); }}
                />
              )}
            />
          </div>
        </div>

        <div className="drawer-field">
          <label htmlFor={`drawer-value-${fieldId}`}>Value (&#163;)</label>
          <CurrencyInput
            id={`drawer-value-${fieldId}`}
            name={`${prefix}.currentValue`}
            control={control}
            onBlur={onFieldBlur}
            aria-invalid={currentValueError ? true : undefined}
            aria-describedby={currentValueError ? valueErrorId : undefined}
          />
          {currentValueError && <span id={valueErrorId} className="field-error-inline">Value required</span>}
        </div>

        {isBond && (
          <div className="drawer-bond-section">
            {!hasBondDetails ? (
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setValue(`${prefix}.bondDetails`, {
                    maturityYear: new Date().getFullYear() + 5,
                    annualCouponPct: 0.04,
                    nominalValue: getValues(`${prefix}.currentValue`) || 10000,
                    issuerType: 'GILT',
                  });
                  onFieldBlur();
                }}
              >
                + Add maturity &amp; coupon details (individual bond)
              </button>
            ) : (
              <>
                <div className="drawer-row">
                  <div className="drawer-field">
                    <label htmlFor={`drawer-maturity-${fieldId}`}>Maturity year</label>
                    <input
                      id={`drawer-maturity-${fieldId}`}
                      type="number"
                      className="form-input"
                      min={2025}
                      max={2100}
                      {...register(`${prefix}.bondDetails.maturityYear`, { valueAsNumber: true })}
                      onBlur={onFieldBlur}
                    />
                  </div>
                  <div className="drawer-field">
                    <label htmlFor={`drawer-coupon-${fieldId}`}>Coupon rate (%)</label>
                    <Controller
                      name={`${prefix}.bondDetails.annualCouponPct`}
                      control={control}
                      render={({ field: couponField }) => {
                        const raw = couponField.value as number | undefined;
                        const display = raw != null && raw > 0 ? (raw * 100).toString() : '';
                        return (
                          <input
                            id={`drawer-coupon-${fieldId}`}
                            type="number"
                            className="form-input"
                            placeholder="e.g. 4.25"
                            step="0.01"
                            min="0"
                            max="20"
                            value={display}
                            onChange={(e) => {
                              const text = e.target.value;
                              if (text === '') { couponField.onChange(0); return; }
                              const pct = Number.parseFloat(text);
                              if (!Number.isNaN(pct) && pct >= 0 && pct <= 20) couponField.onChange(pct / 100);
                            }}
                            onBlur={() => { couponField.onBlur(); onFieldBlur(); }}
                          />
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="drawer-row">
                  <div className="drawer-field">
                    <label htmlFor={`drawer-nominal-${fieldId}`}>Units (nominal value)</label>
                    <CurrencyInput
                      id={`drawer-nominal-${fieldId}`}
                      name={`${prefix}.bondDetails.nominalValue`}
                      control={control}
                      onBlur={onFieldBlur}
                    />
                  </div>
                  <div className="drawer-field">
                    <label htmlFor={`drawer-issuer-${fieldId}`}>Type</label>
                    <select
                      id={`drawer-issuer-${fieldId}`}
                      className="form-input"
                      {...register(`${prefix}.bondDetails.issuerType`)}
                      onBlur={onFieldBlur}
                    >
                      <option value="GILT">UK Gilt</option>
                      <option value="CORPORATE">Corporate Bond</option>
                    </select>
                  </div>
                </div>
                {bondCoupon != null && bondCoupon > 0 && bondNominal != null && bondNominal > 0 && (
                  <p className="bond-income-hint">Approx. &#163;{formatNumber(Math.round(bondCoupon * bondNominal))}/year</p>
                )}
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => { setValue(`${prefix}.bondDetails`, undefined); onFieldBlur(); }}
                >
                  Remove bond details
                </button>
                <p className="bond-disclaimer">Coupon income is modelled as annual cash income. The bond is assumed held to maturity.</p>
              </>
            )}
          </div>
        )}

        {!isBond && (
          <div className="drawer-disclosure">
            {!hasQuantityFields ? (
              <button
                type="button"
                className="btn-link quantity-details-toggle"
                disabled={livePriceLoading}
                onClick={onEnableQuantityTracking}
              >
                {livePriceLoading ? 'Estimating units from today’s price...' : '+ I know my unit count'}
              </button>
            ) : (
              <>
                <p className="drawer-section-label">Unit count</p>
                <div className="drawer-row">
                  <div className="drawer-field">
                    <label htmlFor={`drawer-qty-${fieldId}`}>Units held</label>
                    <input
                      id={`drawer-qty-${fieldId}`}
                      type="number"
                      className="form-input"
                      step="any"
                      min="0"
                      {...register(`${prefix}.quantity`, { valueAsNumber: true, onChange: onQuantityFieldChange })}
                      onBlur={onFieldBlur}
                    />
                    {quantityEstimated && (
                      <p className="quantity-estimate-hint">
                        Estimated from your current total &#247; today&apos;s price &mdash; adjust if you know the exact figure.
                      </p>
                    )}
                  </div>
                  <div className="drawer-field">
                    <label htmlFor={`drawer-price-${fieldId}`}>
                      Price per unit (&#163;) <span className="field-note">today</span>
                      {priceIsLive && <span className="ticker-auto-badge visible" title="Fetched from today's price">live</span>}
                    </label>
                    <div className="drawer-inline-fetch">
                      <CurrencyInput
                        id={`drawer-price-${fieldId}`}
                        name={`${prefix}.unitPrice`}
                        control={control}
                        decimals={2}
                        onValueChange={onUnitPriceFieldChange}
                        onBlur={onFieldBlur}
                      />
                      {holdingValues?.ticker && (
                        <button
                          type="button"
                          className="drawer-mini-fetch"
                          disabled={livePriceLoading}
                          onClick={onFetchLivePrice}
                          title="Refresh today's price for this ticker"
                        >
                          {livePriceLoading ? '…' : '↻'}
                        </button>
                      )}
                    </div>
                    {priceIsLive && livePriceInfo?.exchange && (
                      <p className="live-price-exchange">
                        {livePriceInfo.tradeCurrency && livePriceInfo.tradeCurrency !== 'GBP' && livePriceInfo.tradePrice != null
                          ? `${livePriceInfo.exchange} · ${formatNativePrice(livePriceInfo.tradePrice, livePriceInfo.tradeCurrency)} → converted to £ at today's rate`
                          : livePriceInfo.exchange}
                      </p>
                    )}
                    {/* The fetch-failure notice is rendered once, under the Ticker
                        field above, so it shows whether or not this section is open (B2). */}
                  </div>
                </div>

                <div className="drawer-row">
                  <div className="drawer-field">
                    <label htmlFor={`drawer-bookprice-${fieldId}`}>
                      Book price per unit (&#163;) <span className="field-note">what you paid</span>
                    </label>
                    <BookPriceInput
                      id={`drawer-bookprice-${fieldId}`}
                      quantity={holdingValues?.quantity}
                      costBasis={holdingValues?.costBasis}
                      onCostBasisChange={onCostBasisChange}
                      onBlur={onFieldBlur}
                    />
                  </div>
                  <div className="drawer-field">
                    <label htmlFor={`drawer-costbasis-readonly-${fieldId}`}>Cost basis</label>
                    <input
                      id={`drawer-costbasis-readonly-${fieldId}`}
                      className="form-input"
                      disabled
                      value={holdingValues?.costBasis != null ? `£${formatNumber(holdingValues.costBasis)}` : ''}
                    />
                  </div>
                </div>
                <p className="drawer-hint">
                  {holdingValues?.accountType === 'BROKERAGE'
                    ? 'Used to work out Capital Gains Tax when this is eventually sold. Cost basis is units × book price — averaged across purchases, not per-purchase HMRC share-matching rules.'
                    : 'Not used for tax here — gains inside a SIPP or ISA aren’t taxed — but it lets us show your gain or loss on this holding over time.'}
                </p>

                <p className="drawer-hint">
                  Value is calculated automatically from units &times; price. You can still type a value directly
                  in the Value field above if you don&apos;t know the exact price &mdash; this will override the calculation.
                </p>

                <button
                  type="button"
                  className="btn-link"
                  onClick={onRemoveQuantityTracking}
                >
                  Stop tracking units &mdash; switch back to entering value directly
                </button>
              </>
            )}
          </div>
        )}

        {!isBond && !hasQuantityFields && holdingValues?.accountType === 'BROKERAGE' && (
          <div className="drawer-field">
            <label htmlFor={`drawer-costbasis-${fieldId}`}>Amount originally invested (&#163;) <span className="field-note">cost basis</span></label>
            <CurrencyInput
              id={`drawer-costbasis-${fieldId}`}
              name={`${prefix}.costBasis`}
              control={control}
              onBlur={onFieldBlur}
            />
            <p className="drawer-hint">Used to work out Capital Gains Tax when this is eventually sold.</p>
          </div>
        )}

        {mode === 'add' && (
          <div className="drawer-save-another">
            <button type="button" className="btn-save-another" onClick={onSaveAndAddAnother}>
              Save &amp; add another
            </button>
          </div>
        )}

        <div className="drawer-delete">
          <button
            type="button"
            className={`btn-delete-text ${confirmDelete ? 'confirming' : ''}`}
            onClick={handleDeleteClick}
          >
            {confirmDelete ? 'Click again to confirm' : 'Delete holding'}
          </button>
        </div>
      </div>
    </>
  );
}

export default HoldingEditDrawer;
